import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, formatINR } from "@/lib/api";

const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], categories: [], blogs: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ("");
      setResults({ products: [], categories: [], blogs: [] });
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) {
      setResults({ products: [], categories: [], blogs: [] });
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data);
      } catch {}
      setLoading(false);
    }, 220);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const go = (to) => { onOpenChange(false); navigate(to); };

  const totalResults = results.products.length + results.categories.length + results.blogs.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] bg-cream p-0 gap-0 overflow-hidden border-border" data-testid="search-dialog">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search size={18} className="text-mauve" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, collections, journal…"
            className="flex-1 bg-transparent outline-none font-body text-base text-charcoal placeholder:text-mauve"
            data-testid="search-input"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-mauve hover:text-charcoal" aria-label="clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-5 py-10 text-center text-mauve">
              <p className="font-cormorant text-lg mb-4">Start typing to explore rieave</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {["Necklaces", "Rose gold", "Best sellers", "Gifting", "Rings under ₹3000"].map((s) => (
                  <button key={s} onClick={() => setQ(s)} className="px-3 py-1.5 rounded-full bg-blush/50 text-charcoal font-label text-[11px] uppercase tracking-[0.2em] hover:bg-blush">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : loading && !totalResults ? (
            <p className="px-5 py-10 text-center text-mauve font-cormorant">Searching…</p>
          ) : totalResults === 0 ? (
            <p className="px-5 py-10 text-center text-mauve font-cormorant">No matches — try a different word.</p>
          ) : (
            <AnimatePresence mode="popLayout">
              {results.categories.length > 0 && (
                <motion.div key="cats" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-3">
                  <p className="eyebrow mb-2">Collections</p>
                  <div className="flex gap-2 flex-wrap">
                    {results.categories.map((c) => (
                      <button key={c.id} onClick={() => go(`/shop/${c.slug}`)} className="px-3 py-1.5 rounded-full bg-lavender/60 font-label text-[11px] uppercase tracking-[0.2em] text-charcoal hover:bg-lavender">
                        {c.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {results.products.length > 0 && (
                <motion.div key="prods" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-3 border-t border-border">
                  <p className="eyebrow mb-3">Products ({results.products.length})</p>
                  <div className="space-y-2">
                    {results.products.map((p) => (
                      <button key={p.id} onClick={() => go(`/product/${p.slug}`)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white transition text-left" data-testid={`search-result-${p.slug}`}>
                        <div className="w-12 h-14 rounded-md overflow-hidden bg-blush/40 flex-shrink-0">
                          {(p.images || [])[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-cormorant text-base text-charcoal leading-tight truncate">{p.name}</p>
                          <p className="font-label text-[10px] uppercase tracking-[0.2em] text-mauve">{p.category_slug}</p>
                        </div>
                        <p className="font-body text-sm text-charcoal">{formatINR(p.price)}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {results.blogs.length > 0 && (
                <motion.div key="blogs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-3 border-t border-border">
                  <p className="eyebrow mb-2">Journal</p>
                  <div className="space-y-1">
                    {results.blogs.map((b) => (
                      <button key={b.id} onClick={() => go(`/blog/${b.slug}`)} className="w-full text-left p-2 rounded-lg hover:bg-white transition">
                        <p className="font-cormorant text-base text-charcoal">{b.title}</p>
                        <p className="text-xs text-mauve truncate">{b.excerpt}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>

        {totalResults > 0 && q.trim() && (
          <div className="border-t border-border px-5 py-3 bg-blush/30">
            <button onClick={() => go(`/shop?search=${encodeURIComponent(q.trim())}`)} className="font-label text-[11px] uppercase tracking-[0.22em] text-rose hover:text-charcoal">
              See all results for "{q}" →
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
